import EmptyState from 'components/EmptyState';
import Loader from 'components/Loader';
import { CustomTableEmptyStateProps } from 'components/tables/list-table/ListTableTypes';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import { useCallback, useMemo } from 'react';
import { useLazyGetHoursByEmployeeAndDayReportQuery } from 'store/api/reportingPerformance';
import { HoursByEmployeeAndDayRow, HoursByEmployeeAndDayTotals } from 'types/reporting/performance';
import { ReportPage } from 'types/reporting/reportPage';
import { ReportLayout } from '../ReportLayout';
import { ReportRowLimitNote } from '../ReportRowLimitNote';
import { ReportEntry } from '../reportCatalog';
import { HoursByDayTable } from './HoursByDayTable';
import { buildFilterConfig, INITIAL_SEARCH_PARAMS, useIsRemunerationOffered } from './hoursByEmployeeAndDayFilters';

type Row = HoursByEmployeeAndDayRow;
type Totals = HoursByEmployeeAndDayTotals;

export function HoursByEmployeeAndDayReport({ report }: { report: ReportEntry }) {
    const [fetchData, { data, isFetching, isError }] = useLazyGetHoursByEmployeeAndDayReportQuery();
    const isRemunerationOffered = useIsRemunerationOffered();
    const filterConfig = useMemo(() => buildFilterConfig(isRemunerationOffered), [isRemunerationOffered]);
    const totals = data?.totals;
    const renderTable = useCallback(
        (
            rows: Row[],
            initialLoad: boolean,
            noData: boolean,
            textId?: string,
            _paginationProps?: unknown,
            _handleLoadMore?: () => void,
            emptyStateProps?: CustomTableEmptyStateProps
        ) => {
            if (initialLoad || (!totals && isFetching)) return <Loader />;
            if (noData || !totals) {
                return (
                    <EmptyState
                        size="page"
                        variant={emptyStateProps?.variant ?? 'empty'}
                        titleId={textId}
                        bodyId="reporting.no-rows-body"
                        surface={textId}
                        onClearFilters={emptyStateProps?.onClearFilters}
                        onRetry={emptyStateProps?.onRetry}
                    />
                );
            }
            return <HoursByDayTable rows={rows} from={totals.from} to={totals.to} totals={totals.lines} />;
        },
        [isFetching, totals]
    );
    return (
        <ReportLayout report={report}>
            <ListTableWithFilters<Row, ReportPage<Row, Totals>>
                data={data}
                belowQuickFilters={<ReportRowLimitNote report={report} rowCount={data?.content.length} />}
                isLoading={isFetching}
                isError={isError}
                fetchData={fetchData}
                filterConfig={filterConfig}
                initialSearchParams={INITIAL_SEARCH_PARAMS}
                renderCustomTable={renderTable}
                pluralTextId="reporting.employees"
                singularTextId="reporting.employee"
                noEntriesId="reporting.no-rows"
                noEntriesBodyId="reporting.no-rows-body"
            />
        </ReportLayout>
    );
}
